/* eslint-disable import/no-cycle */
/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { isBoolean } from '@vueuse/core';

import { useOrderFormStore } from '@/stores/forms/orderFormStore';

import usePaymentEndpoints from '@/composables/api/usePaymentEndpoints';
import usePaymentFilters from '@/composables/filters/usePaymentFilters';
import useOrderEndpoints from '@/composables/api/useOrderEndpoints';
import { getCountries } from '@/composables/api/useCountryEndpoints';
import { useTrackingStore } from '@/stores/tracking/trackingStore';
import useSessionStorage from '@/composables/storage/useSessionStorage';
import useLocalStorage from '@/composables/storage/useLocalStorage';

const getSessionStorage = () => useSessionStorage();
const getLocalStorage = () => useLocalStorage();
const getPaymentEndpoints = () => usePaymentEndpoints();
const getPaymentFilters = () => usePaymentFilters();
const getOrderEndpoints = () => useOrderEndpoints();

export const ORDER_TYPE = {
  BUY: 'BUY',
  SELL: 'SELL',
};

export const globalOptions = {
  payments: [],
  payouts: [],
  nftPayments: [],
  customerId: '',
  customerReference: '',
  showSettings: false,
  theme: null,
  merchant: null,
  isFormLoading: false,
  detectedGeo: null,
};

export const useGlobalStore = defineStore({
  id: 'global',
  state: () => ({
    global: getSessionStorage().checkSessionStorage('global', globalOptions),
    fetchingToken: false,
    pendingTokenPromise: null,
    token: null,
    sessionToken: null,
    // @deprecated EXPRESS_CHECKOUT — flow no longer supported; remove with <TICKET>.
    expressCheckoutEnabled: false,
    language: localStorage.getItem('locale'),
    countries: [],
    subdivisions: [],
    coins: [{
      coin_code: 'BTC',
      blockchains: [{
        code: 'BTC',
        name: 'Bitcoin',
        address_tag: {
          regex: null,
          required: false,
          name: null,
        },
      }],
    }],
  }),
  actions: {
    getSessionToken() {
      const storedToken = getLocalStorage().checkLocalStorage('token', false);

      if (storedToken) {
        this.sessionToken = storedToken;
      }
    },
    setCountries(countries) {
      this.countries = countries;
    },
    setSubdivisions(subdivisions) {
      this.subdivisions = subdivisions;
    },
    setCoins(coins) {
      this.coins = coins;
    },
    setLanguage(language) {
      this.language = language;
      getSessionStorage().bindSessionStorage('locale', this.language);
    },
    setIsFormLoading(isLoading) {
      this.global.isFormLoading = isLoading;
    },
    setMerchantSettings(settings) {
      const orderStore = useOrderFormStore();
      this.global.merchant = settings;

      const { defaultCoin } = this.global.merchant;

      if (defaultCoin) orderStore.updateDefaultCoin(defaultCoin);
    },
    /**
     * @deprecated Express Checkout flow is no longer supported. The
     * `expressCheckoutEnabled` flag is no longer read by anything (the
     * `handleExpressCheckout` helpers always return false). Remove with <TICKET>.
     */
    setExpressCheckoutEnabled(value = false) {
      this.expressCheckoutEnabled = value;
    },
    toggleSettings(value) {
      if (isBoolean(value)) {
        this.global.showSettings = value;
      }
      if (value === undefined) {
        this.global.showSettings = Boolean(!this.global.showSettings);
      }
      getSessionStorage().bindSessionStorage(this.$id, this.global);
    },
    toggleTheme() {
      // eslint-disable-next-line no-nested-ternary
      const newTheme = this.global.theme === undefined
          ? 'DARK'
          : this.global.theme === 'LIGHT'
          ? 'DARK'
          : 'LIGHT';
      this.global.theme = newTheme;
      getSessionStorage().bindSessionStorage(this.$id, this.global);
    },
    setTheme(theme) {
      this.global.theme = theme;
      getSessionStorage().bindSessionStorage(this.$id, this.global);
    },
    async fetchCountries() {
      const res = await getCountries();
      const countries = res.data.data.countries.map((country) => {
        // once HE-4830 and HE-4831 have been deployed, we can remove this first condition
        if (Array.isArray(country)) {
          return {
            name: country[0],
            code: country[1].toUpperCase(),
            collectSubdivision: 0,
          };
        }
        return {
          name: country.name,
          code: country.code.toUpperCase(),
          collectSubdivision: country.collectSubdivision,
        };
      });

      // eslint-disable-next-line no-nested-ternary,vue/max-len
      this.countries = countries.sort((itemA, itemB) => (itemA.name < itemB.name ? -1 : itemA.name > itemB.name ? 1 : 0));
    },

    async fetchPayments() {
      const orderStore = useOrderFormStore();
      const { getPayments } = getPaymentEndpoints();
      const { removeApplepayByUserSession } = getPaymentFilters();

      const res = await getPayments();

      const {
        payment_methods: paymentMethods,
        payouts,
        nft_payments: nftPayments,
      } = res.data.data;
      if (paymentMethods) {
        const filteredPayments = removeApplepayByUserSession(paymentMethods);
        this.global.payments = filteredPayments;

        if (!payouts) {
          orderStore.setActiveMode('BUY');
        }
      }

      if (payouts) {
        if (!paymentMethods) {
          orderStore.setActiveMode('SELL');
        }
        this.global.payouts = payouts;
      }

      if (nftPayments) {
        const filterdNFTPayments = removeApplepayByUserSession(nftPayments);
        this.global.nftPayments = filterdNFTPayments;
      }
      getSessionStorage().bindSessionStorage(this.$id, this.global);
    },
    setCustomerId(customerId) {
      this.global.customerId = customerId;
      getSessionStorage().bindSessionStorage(this.$id, this.global);
      if (customerId) {
        const trackingStore = useTrackingStore();
        trackingStore.recordCustomer(customerId);
      }
    },
    setCustomerReference(customerReference) {
      this.global.customerReference = customerReference;
      getSessionStorage().bindSessionStorage(this.$id, this.global);
    },
    setDetectedGeo(geo) {
      this.global.detectedGeo = geo;
      getSessionStorage().bindSessionStorage(this.$id, this.global);
    },
    setDefaultFiat(fiat) {
      this.global.defaultFiat = fiat;
      getSessionStorage().bindSessionStorage(this.$id, this.global);
    },
  },
  getters: {
    getPayments: (store) => store.global.payments,
    getPayouts: (store) => store.global.payouts,
    getNFTPayments: (store) => store.global.nftPayments,
    getCountries: (store) => store.countries,
    getCoins: async (store) => {
      const orderStore = useOrderFormStore();
      const { getCoins } = getOrderEndpoints();
      const coinResponse = await getCoins(String(orderStore.getActiveMode).toLowerCase());

      store.setCoins(coinResponse?.data?.data?.coins);
      return store.coins;
    },
    getSubdivisions: (store) => store.subdivisions,
    getCountryCode: (store) => async (countryName) => {
      if (store.countries.length === 0) {
        await store.fetchCountries();
      }

      const findMatchingCountry = store.countries.find(
        (country) => country.name === countryName,
      );
      if (!findMatchingCountry) {
        return countryName;
      }

      return findMatchingCountry.code;
    },
    getCountryName: (store) => (countryCode) => {
      const findMatchingCountry = store.countries.find(
        (country) => country.code === countryCode,
      );
      if (!findMatchingCountry) {
        return countryCode;
      }

      return findMatchingCountry.name;
    },
    getSubdivisionByCountry: (store) => (countryCode) => {
      const findMatchingCountry = store.countries.find(
        (country) => country.code === countryCode,
      );

      if (!findMatchingCountry) {
        return null;
      }

      return findMatchingCountry?.collectSubdivision;
    },
    getCountriesWithSubdivisions: (store) => store.countries.filter(
      (country) => country.collectSubdivision === 1,
    ),
    getIsLightMode: (store) => store.global.theme === 'LIGHT',
    getIsDarkMode: (store) => store.global.theme === 'DARK',
    getIsThemeSet: (store) => store.global.theme !== null,
    getCustomerId: (store) => store.global.customerId,
    getCustomerReference: (store) => store.global.customerReference,
    getMerchantSettings: (store) => store.global.merchant,
    getIsFormLoading: (store) => store.global.isFormLoading,
    getIsInIFrame: () => {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    },
    /**
     * Partner iframe: minimal button-only UI when merchant enables
     * `embeddedButtonOnlyIframeEnabled` (from get-base-merchant-settings) in an iframe.
     * Therefore checking both conditions:
     * 1. merchant.embeddedButtonOnlyIframeEnabled is true
     * 2. window.self !== window.top (app runs in an iframe)
     */
    getEmbeddedButtonOnlyIframeMode: (store) => {
      if (!store.global.merchant?.embeddedButtonOnlyIframeEnabled) {
        return false;
      }
      try {
        // app runs in an iframe
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    },
    getEmbeddedAPMerchantDomain: (store) => store.global.merchant?.embeddedAPMerchantDomain,
    getIsBanxaNative: (store) => !!(store.global.merchant?.theme?.hide_logo === true),
    hideBgOnBanxaNativeAndNoMerchBg: (store) => store.getIsBanxaNative
    && !store.global.merchant?.theme?.background,
    getDetectedGeo: (store) => store.global.detectedGeo,
    getIsAutoRedirectToMerchant:
      (store) => store.global.merchant?.shouldAutoRedirectToMerchant === true,
    getIsKyb: (store) => store.global?.merchant?.corporateOnlyMerchant === true,
  },
});
